import { collection, getDocsFromServer, onSnapshot } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrgRole } from '@finvox/shared/org';
import {
  memberPermissionsFromMemberDoc,
  type MemberPermissionOverrides
} from '@finvox/shared/member-access';
import { AddUserDialog } from '@/components/settings/AddUserDialog';
import { MemberPermissionsEditor } from '@/components/settings/MemberPermissionsEditor';
import { shouldSkipOrgLiveUpdate } from '@/lib/orgSnapshotGuard';
import { getFirestoreDb } from '@/lib/firebase';
import {
  mapProvisionError,
  provisionOrgMember,
  removeOrgMember,
  updateOrgMemberPermissions,
  updateOrgMemberRole
} from '@/lib/membersFirestore';
import { useToast } from '@/contexts/ToastContext';
import { toastError } from '@/lib/toastErrors';
import { useSettingsAccess } from '@/components/settings/useSettingsAccess';
import { SettingsFirestoreError } from '@/components/settings/SettingsFirestoreError';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { SettingsSection } from '@/components/settings/SettingsSection';

const MEMBER_ROLES = ['accountant', 'bookkeeper', 'viewer'] as const satisfies readonly OrgRole[];

type MemberRow = {
  uid: string;
  email: string;
  role: OrgRole;
  permissions?: MemberPermissionOverrides;
  cashbookPermissions?: MemberPermissionOverrides['cashbook'];
};

function roleHintKey(role: OrgRole): string {
  return `settings.roles.${role}Hint`;
}

function mapMemberRows(
  docs: { id: string; data: () => Record<string, unknown> }[]
): MemberRow[] {
  return docs
    .map((d) => {
      const { overrides, legacyCashbook } = memberPermissionsFromMemberDoc(d.data());
      return {
        uid: d.id,
        email: String(d.data().email ?? ''),
        role: (d.data().role as OrgRole) ?? 'viewer',
        permissions: overrides,
        cashbookPermissions: legacyCashbook
      };
    })
    .sort((a, b) => a.email.localeCompare(b.email, undefined, { sensitivity: 'base' }));
}

export function UsersSection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { orgId, online, canManageUsers } = useSettingsAccess();
  const toast = useToast();
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [busyUid, setBusyUid] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId || !canManageUsers || !user?.uid) {
      setMembers([]);
      setLoading(false);
      return;
    }
    const uid = user.uid;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const col = collection(getFirestoreDb(), 'organizations', orgId, 'members');

    void getDocsFromServer(col)
      .then((snap) => {
        if (cancelled) return;
        setMembers(mapMemberRows(snap.docs));
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setMembers([]);
        setLoading(false);
      });

    const unsub = onSnapshot(
      col,
      (snap) => {
        if (cancelled || shouldSkipOrgLiveUpdate(snap.metadata, uid, orgId)) return;
        setMembers(mapMemberRows(snap.docs));
        setLoading(false);
        setError(null);
      },
      (e) => {
        if (cancelled) return;
        setError(e.message);
        setMembers([]);
        setLoading(false);
      }
    );

    return () => {
      cancelled = true;
      unsub();
    };
  }, [orgId, canManageUsers, user?.uid]);

  async function addUser(payload: {
    email: string;
    password: string;
    role: OrgRole;
    permissions: MemberPermissionOverrides;
  }) {
    if (!orgId || !online || !user?.uid) return;
    setAdding(true);
    try {
      await provisionOrgMember(
        orgId,
        user.uid,
        payload.email,
        payload.password,
        payload.role,
        Object.keys(payload.permissions).length ? payload.permissions : null
      );
      toast.success(t('settings.addUserSuccess'), 'settings-users');
      setAddDialogOpen(false);
    } catch (e) {
      const mapped = mapProvisionError(e);
      if (mapped.message === 'Email already in use') {
        toast.error(t('settings.addUserEmailInUse'), 'settings-users');
      } else if (mapped.message === 'Invalid email') {
        toast.error(t('settings.addUserInvalidEmail'), 'settings-users');
      } else if (mapped.message === 'Password must be at least 6 characters') {
        toast.error(t('settings.addUserWeakPassword'), 'settings-users');
      } else {
        toastError(toast, mapped, 'settings-users');
      }
    } finally {
      setAdding(false);
    }
  }

  async function updateRole(targetUid: string, role: OrgRole) {
    if (!orgId || !online) return;
    setBusyUid(targetUid);
    try {
      await updateOrgMemberRole(orgId, targetUid, role);
      toast.success(t('settings.memberRoleUpdated'), 'settings-users-role');
    } catch (e) {
      toastError(toast, e, 'settings-users-role');
    } finally {
      setBusyUid(null);
    }
  }

  async function saveMemberPermissions(targetUid: string, permissions: MemberPermissionOverrides) {
    if (!orgId || !online) return;
    setBusyUid(targetUid);
    try {
      await updateOrgMemberPermissions(orgId, targetUid, permissions);
      toast.success(t('settings.memberPermissionsSaved'), 'settings-users-permissions');
    } catch (e) {
      toastError(toast, e, 'settings-users-permissions');
    } finally {
      setBusyUid(null);
    }
  }

  async function resetMemberPermissions(targetUid: string) {
    if (!orgId || !online) return;
    setBusyUid(targetUid);
    try {
      await updateOrgMemberPermissions(orgId, targetUid, null);
      toast.success(t('settings.memberPermissionsReset'), 'settings-users-permissions-reset');
    } catch (e) {
      toastError(toast, e, 'settings-users-permissions-reset');
    } finally {
      setBusyUid(null);
    }
  }

  async function removeMember(targetUid: string) {
    if (!orgId || !online || !user?.uid) return;
    if (!window.confirm(t('settings.removeMemberConfirm'))) return;
    setBusyUid(targetUid);
    try {
      await removeOrgMember(orgId, user.uid, targetUid);
      toast.success(t('settings.memberRemoved'), 'settings-users-remove');
    } catch (e) {
      toastError(toast, e, 'settings-users-remove');
    } finally {
      setBusyUid(null);
    }
  }

  if (!canManageUsers) return null;

  return (
    <>
      <SettingsSection
        id="settings-users"
        title={t('settings.users')}
        description={t('settings.usersHint')}
        loading={loading}
        action={
          <Button disabled={!online || !orgId} onClick={() => setAddDialogOpen(true)}>
            {t('settings.addUser')}
          </Button>
        }
      >
        <SettingsFirestoreError message={error ?? undefined} />

        {members.length === 0 && !loading ? (
          <p className="text-sm text-[var(--fv-text-muted)]">{t('settings.usersEmpty')}</p>
        ) : (
          <ul className="divide-y divide-[var(--fv-border)] rounded-[var(--fv-radius)] border border-[var(--fv-border)] text-sm">
            {members.map((m) => (
              <li
                key={m.uid}
                className="flex flex-col gap-3 px-4 py-3 transition-colors hover:bg-[var(--fv-bg)] sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="truncate font-medium text-[var(--fv-text)]">{m.email}</span>
                    <span className="text-xs text-[var(--fv-text-muted)]">
                      {m.role === 'owner'
                        ? t('settings.roles.owner')
                        : t(`settings.roles.${m.role}`)}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-[var(--fv-text-muted)]">{t(roleHintKey(m.role))}</p>
                  {m.role !== 'owner' ? (
                    <MemberPermissionsEditor
                      role={m.role}
                      permissions={m.permissions}
                      legacyCashbook={m.cashbookPermissions}
                      online={online}
                      busy={busyUid === m.uid}
                      onSave={(permissions) => saveMemberPermissions(m.uid, permissions)}
                      onReset={() => resetMemberPermissions(m.uid)}
                    />
                  ) : null}
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                  {m.role === 'owner' ? null : (
                    <Select
                      value={m.role}
                      disabled={!online || busyUid === m.uid}
                      onValueChange={(v) => void updateRole(m.uid, v as OrgRole)}
                      options={MEMBER_ROLES.map((r) => ({
                        value: r,
                        label: t(`settings.roles.${r}`)
                      }))}
                    />
                  )}
                  {m.uid !== user?.uid ? (
                    <Button
                      variant="link"
                      size="sm"
                      disabled={!online || busyUid === m.uid}
                      onClick={() => void removeMember(m.uid)}
                    >
                      {t('settings.removeMember')}
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </SettingsSection>

      <AddUserDialog
        open={addDialogOpen}
        busy={adding}
        online={online}
        onClose={() => {
          if (!adding) setAddDialogOpen(false);
        }}
        onSave={addUser}
      />
    </>
  );
}
